import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { SimpleSelectField } from '@unite-us/app-components';
import {
  propagateSelectBlur,
  propagateSelectChange,
  resolveSelectedValue,
} from './selectFieldHelpers';
import {
  buildServiceTypeNextValue,
  candidateMatchesFilter,
  createFilterCache,
  normalizeServiceTypeOptions,
} from './serviceTypeSelectHelpers';

const ServiceTypeSelectField = ({
  disabled,
  error,
  field,
  onChange,
  options,
  placeholder,
  searchChoices,
  ...props
}) => {
  const filterCacheRef = useRef({ inputValue: '', ids: null });
  const normalizedOptions = useMemo(
    () => normalizeServiceTypeOptions(options),
    [options],
  );

  const value = useMemo(
    () => resolveSelectedValue(field?.value),
    [field?.value],
  );

  const handleChange = useCallback((selectedId) => {
    const nextValue = buildServiceTypeNextValue(selectedId, normalizedOptions);
    const inputRef = { current: field };
    propagateSelectChange({ inputRef, onChange, value: nextValue });
  }, [field, normalizedOptions, onChange]);

  const handleBlur = useCallback(
    () => propagateSelectBlur({ current: field }),
    [field],
  );

  const filterOption = useCallback((candidate, inputValue) => {
    if (!inputValue) return true;
    if (filterCacheRef.current.inputValue !== inputValue) {
      filterCacheRef.current = createFilterCache(options, inputValue);
    }
    return candidateMatchesFilter(candidate, filterCacheRef.current);
  }, [options]);

  return (
    <SimpleSelectField
      disabled={disabled}
      error={error}
      onBlur={handleBlur}
      onChange={handleChange}
      options={normalizedOptions}
      placeholder={placeholder}
      searchable={searchChoices}
      value={value}
      filterOption={filterOption}
      clearable={false}
      {...props}
    />
  );
};

ServiceTypeSelectField.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  field: PropTypes.object,
  id: PropTypes.string,
  inline: PropTypes.bool,
  label: PropTypes.string,
  labelKey: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  searchChoices: PropTypes.bool,
  searchPlaceholderValue: PropTypes.string,
  touch: PropTypes.func,
  validations: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
  ]),
  valueKey: PropTypes.string,
};

ServiceTypeSelectField.defaultProps = {
  className: '',
  id: '',
  inline: false,
  label: 'Service Type',
  labelKey: 'name',
  onChange: undefined,
  options: [],
  placeholder: '',
  searchChoices: true,
  searchPlaceholderValue: '',
  valueKey: 'id',
  disabled: false,
  error: undefined,
  field: undefined,
  touch: undefined,
  validations: undefined,
};

export default ServiceTypeSelectField;
